import { useState, useEffect } from 'react'
import { Card, Table, Button, Badge, Modal, Form, Row, Col, Alert, Tabs, Tab, Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'
import api from '../services/api'
import { useConfirm } from '../components/common/ConfirmDialog'

interface JailStatus {
  name: string
  enabled: boolean
  currently_failed: number
  total_failed: number
  currently_banned: number
  total_banned: number
  banned_ips: string[]
}

interface Fail2BanStatus {
  running: boolean
  version: string
  jails: string[]
}

interface JailTemplate {
  name: string
  description?: string
  enabled: boolean
  port: string
  filter: string
  logpath: string
  maxretry: number
  findtime: string
  bantime: string
}

interface WhitelistData {
  ips: string[]
  trunk_ips: { ip: string; hostname?: string; trunk_name: string; description: string }[]
  raw_config: string
}

interface GlobalSettings {
  bantime: string
  findtime: string
  maxretry: number
}

interface RecidiveSettings {
  enabled: boolean
  bantime: string
  findtime: string
  maxretry: number
}

interface BanStatistics {
  total_banned_now: number
  total_banned_all_time: number
  total_failed: number
  bans_by_jail: Record<string, { currently_banned: number; total_banned: number; total_failed: number }>
  top_attackers: { ip: string; ban_count: number; country_code: string; country_name: string }[]
  bans_by_country: Record<string, { code: string; name: string; count: number }>
  recidive_banned: number
}

interface BannedIpWithGeo {
  ip: string
  country_code: string
  country_name: string
}

interface IPBlockCountry {
  code: string
  name: string
  ranges: number
  packets_blocked: number
  bytes_blocked: number
}

interface IPBlockStatus {
  blocked_countries: string[]
  blocklist_de_enabled: boolean
  spamhaus_drop_enabled: boolean
  firehol_level1_enabled: boolean
  last_update: string | null
  countries: Record<string, IPBlockCountry>
  blocklist_de: {
    name: string
    ips: number
    packets_blocked: number
    bytes_blocked: number
  } | null
  spamhaus_drop: {
    name: string
    ranges: number
    packets_blocked: number
    bytes_blocked: number
  } | null
  firehol_level1: {
    name: string
    ranges: number
    packets_blocked: number
    bytes_blocked: number
  } | null
  available_countries: Record<string, string>
}

export default function Fail2Ban() {
  const confirm = useConfirm()
  const [status, setStatus] = useState<Fail2BanStatus | null>(null)
  const [jails, setJails] = useState<JailStatus[]>([])
  const [templates, setTemplates] = useState<Record<string, JailTemplate>>({})
  const [logs, setLogs] = useState<string[]>([])
  const [config, setConfig] = useState('')
  const [whitelist, setWhitelist] = useState<WhitelistData>({ ips: [], trunk_ips: [], raw_config: '' })
  const [newWhitelistIp, setNewWhitelistIp] = useState('')
  const [currentIp, setCurrentIp] = useState('')
  const [globalSettings, setGlobalSettings] = useState<GlobalSettings>({ bantime: '1h', findtime: '10m', maxretry: 5 })
  const [recidiveSettings, setRecidiveSettings] = useState<RecidiveSettings>({ enabled: false, bantime: '1w', findtime: '1w', maxretry: 3 })
  const [statistics, setStatistics] = useState<BanStatistics | null>(null)
  const [bannedIpsWithGeo, setBannedIpsWithGeo] = useState<Record<string, BannedIpWithGeo[]>>({})
  const [loading, setLoading] = useState(true)
  const [statsLoading, setStatsLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [ipBlockStatus, setIpBlockStatus] = useState<IPBlockStatus | null>(null)
  const [ipBlockLoading, setIpBlockLoading] = useState(false)
  const [selectedCountryToBlock, setSelectedCountryToBlock] = useState('')

  // Modals
  const [showBanModal, setShowBanModal] = useState(false)
  const [showCreateJailModal, setShowCreateJailModal] = useState(false)
  const [selectedJail, setSelectedJail] = useState('')
  const [banIp, setBanIp] = useState('')

  // New Jail Form
  const [newJail, setNewJail] = useState<JailTemplate>({
    name: '',
    enabled: true,
    port: '',
    filter: '',
    logpath: '',
    maxretry: 5,
    findtime: '10m',
    bantime: '1h'
  })

  const loadData = async () => {
    try {
      const [statusRes, jailsRes, templatesRes, logsRes, configRes, whitelistRes, ipRes, settingsRes, recidiveRes] = await Promise.all([
        api.get('/fail2ban/status'),
        api.get('/fail2ban/jails'),
        api.get('/fail2ban/templates'),
        api.get('/fail2ban/logs?lines=50'),
        api.get('/fail2ban/config'),
        api.get('/fail2ban/whitelist'),
        api.get('/auth/trusted-ips'),
        api.get('/fail2ban/settings'),
        api.get('/fail2ban/recidive')
      ])
      setStatus(statusRes.data)
      setJails(jailsRes.data)
      setTemplates(templatesRes.data)
      setLogs(logsRes.data.logs || [])
      setConfig(configRes.data.content || '')
      setWhitelist(whitelistRes.data)
      setCurrentIp(ipRes.data.current_ip || '')
      setGlobalSettings(settingsRes.data)
      setRecidiveSettings(recidiveRes.data)
    } catch (error) {
      toast.error('Fehler beim Laden der Fail2Ban-Daten')
    } finally {
      setLoading(false)
    }
  }

  const loadStatistics = async () => {
    setStatsLoading(true)
    try {
      const res = await api.get('/fail2ban/statistics')
      setStatistics(res.data)
    } catch (error) {
      toast.error('Fehler beim Laden der Statistiken')
    } finally {
      setStatsLoading(false)
    }
  }

  const loadBannedIpsWithGeo = async (jailName: string) => {
    try {
      const res = await api.get(`/fail2ban/jails/${jailName}/banned-with-geo`)
      setBannedIpsWithGeo(prev => ({ ...prev, [jailName]: res.data }))
    } catch (error) {
      // Silently fail for geo lookup
    }
  }

  const loadIpBlockStatus = async () => {
    setIpBlockLoading(true)
    try {
      const res = await api.get('/fail2ban/ipblock/status')
      setIpBlockStatus(res.data)
    } catch (error) {
      // IP blocking might not be available
      console.error('IP blocking not available:', error)
    } finally {
      setIpBlockLoading(false)
    }
  }

  const handleAddCountryBlock = async () => {
    if (!selectedCountryToBlock) return
    setIpBlockLoading(true)
    try {
      await api.post('/fail2ban/ipblock/country', { country_code: selectedCountryToBlock })
      toast.success(`Land ${selectedCountryToBlock} zur Sperrliste hinzugefügt`)
      setSelectedCountryToBlock('')
      loadIpBlockStatus()
    } catch (error) {
      toast.error('Fehler beim Hinzufügen des Landes')
    } finally {
      setIpBlockLoading(false)
    }
  }

  const handleRemoveCountryBlock = async (countryCode: string) => {
    const confirmed = await confirm({
      title: 'Land entsperren',
      message: `Land ${countryCode} von der Sperrliste entfernen?`,
      confirmText: 'Entfernen',
      variant: 'warning'
    })
    if (!confirmed) return
    setIpBlockLoading(true)
    try {
      await api.delete(`/fail2ban/ipblock/country/${countryCode}`)
      toast.success(`Land ${countryCode} von Sperrliste entfernt`)
      loadIpBlockStatus()
    } catch (error) {
      toast.error('Fehler beim Entfernen des Landes')
    } finally {
      setIpBlockLoading(false)
    }
  }

  const handleToggleBlocklistDe = async (enable: boolean) => {
    setIpBlockLoading(true)
    try {
      if (enable) {
        await api.post('/fail2ban/ipblock/blocklist-de/enable')
        toast.success('blocklist.de aktiviert')
      } else {
        await api.post('/fail2ban/ipblock/blocklist-de/disable')
        toast.success('blocklist.de deaktiviert')
      }
      loadIpBlockStatus()
    } catch (error) {
      toast.error('Fehler beim Ändern von blocklist.de')
    } finally {
      setIpBlockLoading(false)
    }
  }

  const handleToggleSpamhaus = async (enable: boolean) => {
    setIpBlockLoading(true)
    try {
      if (enable) {
        await api.post('/fail2ban/ipblock/spamhaus/enable')
        toast.success('Spamhaus DROP/EDROP aktiviert')
      } else {
        await api.post('/fail2ban/ipblock/spamhaus/disable')
        toast.success('Spamhaus DROP/EDROP deaktiviert')
      }
      loadIpBlockStatus()
    } catch (error) {
      toast.error('Fehler beim Ändern von Spamhaus')
    } finally {
      setIpBlockLoading(false)
    }
  }

  const handleToggleFirehol = async (enable: boolean) => {
    setIpBlockLoading(true)
    try {
      if (enable) {
        await api.post('/fail2ban/ipblock/firehol/enable')
        toast.success('FireHOL Level 1 aktiviert')
      } else {
        await api.post('/fail2ban/ipblock/firehol/disable')
        toast.success('FireHOL Level 1 deaktiviert')
      }
      loadIpBlockStatus()
    } catch (error) {
      toast.error('Fehler beim Ändern von FireHOL')
    } finally {
      setIpBlockLoading(false)
    }
  }

  const handleUpdateIpBlock = async () => {
    setIpBlockLoading(true)
    try {
      await api.post('/fail2ban/ipblock/update')
      toast.success('IP-Sperrlisten aktualisiert')
      loadIpBlockStatus()
    } catch (error) {
      toast.error('Fehler beim Aktualisieren')
    } finally {
      setIpBlockLoading(false)
    }
  }

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
  }

  useEffect(() => {
    loadData()
    // Refresh every 30 seconds
    const interval = setInterval(loadData, 30000)
    return () => clearInterval(interval)
  }, [])

  const handleServiceAction = async (action: 'start' | 'stop' | 'restart' | 'reload') => {
    setActionLoading(true)
    try {
      await api.post(`/fail2ban/${action}`)
      toast.success(`Fail2Ban ${action === 'start' ? 'gestartet' : action === 'stop' ? 'gestoppt' : action === 'restart' ? 'neugestartet' : 'neu geladen'}`)
      setTimeout(loadData, 2000)
    } catch {
      toast.error(`Fehler bei ${action}`)
    } finally {
      setActionLoading(false)
    }
  }

  const handleUnban = async (jail: string, ip: string) => {
    try {
      await api.post('/fail2ban/unban', { jail, ip })
      toast.success(`${ip} entsperrt`)
      loadData()
    } catch {
      toast.error('Fehler beim Entsperren')
    }
  }

  const handleUnbanAll = async (jail: string) => {
    const confirmed = await confirm({
      title: 'Alle IPs entsperren',
      message: `Alle IPs in "${jail}" entsperren?`,
      confirmText: 'Löschen',
      variant: 'danger'
    })
    if (!confirmed) return
    try {
      await api.post(`/fail2ban/unban-all/${jail}`)
      toast.success('Alle IPs entsperrt')
      loadData()
    } catch {
      toast.error('Fehler beim Entsperren')
    }
  }

  const handleBan = async () => {
    if (!banIp || !selectedJail) return
    try {
      await api.post('/fail2ban/ban', { jail: selectedJail, ip: banIp })
      toast.success(`${banIp} gesperrt`)
      setShowBanModal(false)
      setBanIp('')
      loadData()
    } catch {
      toast.error('Fehler beim Sperren')
    }
  }

  const handleCreateJail = async () => {
    if (!newJail.name) {
      toast.error('Name erforderlich')
      return
    }
    try {
      await api.post('/fail2ban/jails/create', newJail)
      toast.success(`Jail "${newJail.name}" erstellt. Bitte Fail2Ban neu laden.`)
      setShowCreateJailModal(false)
      setNewJail({
        name: '', enabled: true, port: '', filter: '', logpath: '',
        maxretry: 5, findtime: '10m', bantime: '1h'
      })
    } catch {
      toast.error('Fehler beim Erstellen')
    }
  }

  const handleDeleteJail = async (jailName: string) => {
    const confirmed = await confirm({
      title: 'Jail löschen',
      message: `Jail "${jailName}" löschen?`,
      confirmText: 'Löschen',
      variant: 'danger'
    })
    if (!confirmed) return
    try {
      await api.delete(`/fail2ban/jails/${jailName}`)
      toast.success(`Jail "${jailName}" gelöscht. Bitte Fail2Ban neu laden.`)
      loadData()
    } catch {
      toast.error('Fehler beim Löschen')
    }
  }

  const handleSaveConfig = async () => {
    try {
      await api.put('/fail2ban/config', { content: config })
      toast.success('Konfiguration gespeichert. Bitte Fail2Ban neu laden.')
    } catch {
      toast.error('Fehler beim Speichern')
    }
  }

  const handleSaveSettings = async () => {
    try {
      await api.put('/fail2ban/settings', globalSettings)
      toast.success('Einstellungen gespeichert. Bitte Fail2Ban neu laden.')
    } catch {
      toast.error('Fehler beim Speichern')
    }
  }

  const handleSaveRecidive = async () => {
    try {
      await api.put('/fail2ban/recidive', recidiveSettings)
      toast.success('Recidive-Einstellungen gespeichert. Bitte Fail2Ban neu laden.')
    } catch {
      toast.error('Fehler beim Speichern')
    }
  }

  const getCountryFlag = (countryCode: string) => {
    if (!countryCode || countryCode.length !== 2) return ''
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt(0))
    return String.fromCodePoint(...codePoints)
  }

  const sortIpsNumerically = (ips: string[]) => {
    return [...ips].sort((a, b) => {
      const aParts = a.split('.').map(Number)
      const bParts = b.split('.').map(Number)
      for (let i = 0; i < 4; i++) {
        if (aParts[i] !== bParts[i]) {
          return aParts[i] - bParts[i]
        }
      }
      return 0
    })
  }

  const handleSyncTrunkIps = async () => {
    setActionLoading(true)
    try {
      const res = await api.post('/fail2ban/sync-trunk-ips')
      toast.success(res.data.message)
      loadData()
    } catch {
      toast.error('Fehler beim Synchronisieren der Trunk-IPs')
    } finally {
      setActionLoading(false)
    }
  }

  const handleAddToWhitelist = async (ipToAdd?: string) => {
    const ip = ipToAdd || newWhitelistIp.trim()
    if (!ip) {
      toast.error('IP-Adresse eingeben')
      return
    }
    try {
      await api.post('/fail2ban/whitelist', { ip })
      toast.success(`${ip} zur Whitelist hinzugefügt. Bitte Fail2Ban neu laden.`)
      setNewWhitelistIp('')
      loadData()
    } catch {
      toast.error('Fehler beim Hinzufügen zur Whitelist')
    }
  }

  const handleRemoveFromWhitelist = async (ip: string) => {
    const confirmed = await confirm({
      title: 'IP entfernen',
      message: `"${ip}" von der Whitelist entfernen?`,
      confirmText: 'Entfernen',
      variant: 'danger'
    })
    if (!confirmed) return
    try {
      await api.delete(`/fail2ban/whitelist/${encodeURIComponent(ip)}`)
      toast.success(`${ip} von Whitelist entfernt. Bitte Fail2Ban neu laden.`)
      loadData()
    } catch {
      toast.error('Fehler beim Entfernen von der Whitelist')
    }
  }

  const applyTemplate = (templateName: string) => {
    const template = templates[templateName]
    if (template) {
      setNewJail({ ...template })
    }
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p className="mt-2">Lade Fail2Ban-Daten...</p>
      </div>
    )
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>
          <i className="bi bi-shield-lock me-2"></i>
          Fail2Ban
        </h4>
        <div>
          <Button
            variant="outline-success"
            size="sm"
            className="me-2"
            onClick={() => handleServiceAction('start')}
            disabled={actionLoading || status?.running}
          >
            <i className="bi bi-play-fill me-1"></i> Start
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            className="me-2"
            onClick={() => handleServiceAction('stop')}
            disabled={actionLoading || !status?.running}
          >
            <i className="bi bi-stop-fill me-1"></i> Stop
          </Button>
          <Button
            variant="outline-warning"
            size="sm"
            className="me-2"
            onClick={() => handleServiceAction('restart')}
            disabled={actionLoading}
          >
            <i className="bi bi-arrow-clockwise me-1"></i> Neustart
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => handleServiceAction('reload')}
            disabled={actionLoading}
          >
            <i className="bi bi-arrow-repeat me-1"></i> Neu laden
          </Button>
        </div>
      </div>

      {/* Status Card */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={4}>
              <strong>Status:</strong>{' '}
              {status?.running ? (
                <Badge bg="success">Aktiv</Badge>
              ) : (
                <Badge bg="danger">Inaktiv</Badge>
              )}
            </Col>
            <Col md={4}>
              <strong>Version:</strong> {status?.version || 'Unbekannt'}
            </Col>
            <Col md={4}>
              <strong>Aktive Jails:</strong> {jails.length}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Tabs defaultActiveKey="jails" className="mb-4">
        {/* Jails Tab */}
        <Tab eventKey="jails" title={<><i className="bi bi-list-check me-1"></i> Jails</>}>
          <div className="d-flex justify-content-end mb-3">
            <Button variant="primary" size="sm" onClick={() => setShowCreateJailModal(true)}>
              <i className="bi bi-plus-lg me-1"></i> Neues Jail
            </Button>
          </div>

          {jails.length === 0 ? (
            <Alert variant="info">Keine aktiven Jails gefunden.</Alert>
          ) : (
            jails.map(jail => (
              <Card key={jail.name} className="mb-3">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{jail.name}</strong>
                    <Badge bg={jail.enabled ? 'success' : 'secondary'} className="ms-2">
                      {jail.enabled ? 'Aktiv' : 'Inaktiv'}
                    </Badge>
                  </div>
                  <div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={() => {
                        setSelectedJail(jail.name)
                        setShowBanModal(true)
                      }}
                    >
                      <i className="bi bi-plus-circle me-1"></i> IP sperren
                    </Button>
                    {jail.currently_banned > 0 && (
                      <Button
                        variant="outline-warning"
                        size="sm"
                        className="me-2"
                        onClick={() => handleUnbanAll(jail.name)}
                      >
                        Alle entsperren
                      </Button>
                    )}
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDeleteJail(jail.name)}
                    >
                      <i className="bi bi-trash"></i>
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body>
                  <Row className="mb-3">
                    <Col md={3}>
                      <div className="text-center">
                        <div className="fs-4 fw-bold text-warning">{jail.currently_failed}</div>
                        <small className="text-muted">Aktuell fehlgeschlagen</small>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center">
                        <div className="fs-4 fw-bold text-secondary">{jail.total_failed}</div>
                        <small className="text-muted">Gesamt fehlgeschlagen</small>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center">
                        <div className="fs-4 fw-bold text-danger">{jail.currently_banned}</div>
                        <small className="text-muted">Aktuell gesperrt</small>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center">
                        <div className="fs-4 fw-bold text-dark">{jail.total_banned}</div>
                        <small className="text-muted">Gesamt gesperrt</small>
                      </div>
                    </Col>
                  </Row>

                  {jail.banned_ips.length > 0 && (
                    <>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">Gesperrte IPs:</h6>
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={() => loadBannedIpsWithGeo(jail.name)}
                        >
                          <i className="bi bi-geo-alt me-1"></i> Länder laden
                        </Button>
                      </div>
                      <Table size="sm" striped>
                        <thead>
                          <tr>
                            <th>IP-Adresse</th>
                            <th>Land</th>
                            <th style={{ width: '100px' }}>Aktion</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortIpsNumerically(jail.banned_ips).map(ip => {
                            const geoData = bannedIpsWithGeo[jail.name]?.find(g => g.ip === ip)
                            return (
                              <tr key={ip}>
                                <td><code>{ip}</code></td>
                                <td>
                                  {geoData ? (
                                    <span title={geoData.country_name}>
                                      {getCountryFlag(geoData.country_code)} {geoData.country_code}
                                    </span>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                                <td>
                                  <Button
                                    variant="outline-success"
                                    size="sm"
                                    onClick={() => handleUnban(jail.name, ip)}
                                  >
                                    Entsperren
                                  </Button>
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </Table>
                    </>
                  )}
                </Card.Body>
              </Card>
            ))
          )}
        </Tab>

        {/* Whitelist Tab */}
        <Tab eventKey="whitelist" title={<><i className="bi bi-shield-check me-1"></i> Whitelist</>}>
          <Card className="mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>IP-Whitelist (ignoreip)</strong>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleSyncTrunkIps}
                disabled={actionLoading}
              >
                <i className="bi bi-arrow-repeat me-1"></i> Trunk-IPs synchronisieren
              </Button>
            </Card.Header>
            <Card.Body>
              <Alert variant="info" className="mb-3">
                <i className="bi bi-info-circle me-2"></i>
                IPs auf der Whitelist werden von Fail2Ban niemals gesperrt. Localhost (127.0.0.1/8, ::1) ist standardmäßig enthalten.
              </Alert>

              {currentIp && !whitelist.ips.includes(currentIp) && (
                <Alert variant="secondary" className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <i className="bi bi-globe me-2"></i>
                    Ihre aktuelle IP: <code>{currentIp}</code>
                  </div>
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={() => handleAddToWhitelist(currentIp)}
                  >
                    <i className="bi bi-plus-lg me-1"></i> Aktuelle IP hinzufügen
                  </Button>
                </Alert>
              )}

              <Form className="mb-4" onSubmit={(e) => { e.preventDefault(); handleAddToWhitelist(); }}>
                <Row>
                  <Col md={8}>
                    <Form.Control
                      type="text"
                      placeholder="IP-Adresse oder CIDR-Notation eingeben (z.B. 192.168.1.0/24)"
                      value={newWhitelistIp}
                      onChange={(e) => setNewWhitelistIp(e.target.value)}
                    />
                  </Col>
                  <Col md={4}>
                    <Button variant="success" onClick={() => handleAddToWhitelist()} className="w-100">
                      <i className="bi bi-plus-lg me-1"></i> Zur Whitelist hinzufügen
                    </Button>
                  </Col>
                </Row>
              </Form>

              <h6>Manuelle IPs</h6>
              {whitelist.ips.filter(ip => ip !== '127.0.0.1/8' && ip !== '::1').length === 0 ? (
                <Alert variant="secondary" className="mb-4">
                  Keine manuellen IPs auf der Whitelist.
                </Alert>
              ) : (
                <Table striped hover className="mb-4">
                  <thead>
                    <tr>
                      <th>IP-Adresse / Netzwerk</th>
                      <th style={{ width: '120px' }}>Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortIpsNumerically(whitelist.ips.filter(ip => ip !== '127.0.0.1/8' && ip !== '::1')).map(ip => (
                      <tr key={ip}>
                        <td><code>{ip}</code></td>
                        <td>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveFromWhitelist(ip)}
                          >
                            <i className="bi bi-trash me-1"></i> Entfernen
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}

              <h6><i className="bi bi-telephone-forward me-2"></i>Trunk-IPs (automatisch verwaltet)</h6>
              {whitelist.trunk_ips.length === 0 ? (
                <Alert variant="secondary">
                  Keine Trunk-IPs gefunden. Trunk-IPs werden automatisch zur Whitelist hinzugefügt.
                </Alert>
              ) : (
                <Table striped hover>
                  <thead>
                    <tr>
                      <th>IP-Adresse</th>
                      <th>Hostname</th>
                      <th>Trunk</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...whitelist.trunk_ips].sort((a, b) => {
                        const aParts = a.ip.split('.').map(Number)
                        const bParts = b.ip.split('.').map(Number)
                        for (let i = 0; i < 4; i++) {
                          if (aParts[i] !== bParts[i]) return aParts[i] - bParts[i]
                        }
                        return 0
                      }).map((t, idx) => (
                      <tr key={`${t.ip}-${idx}`}>
                        <td><code>{t.ip}</code></td>
                        <td>{t.hostname ? <code>{t.hostname}</code> : <span className="text-muted">-</span>}</td>
                        <td>
                          <Badge bg="info">{t.trunk_name}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}

              <hr className="my-4" />

              <h6>Rohe Konfiguration (whitelist.local)</h6>
              <pre style={{
                backgroundColor: '#f8f9fa',
                padding: '1rem',
                borderRadius: '4px',
                fontSize: '0.85rem'
              }}>
                {whitelist.raw_config || '# Noch keine Whitelist-Konfiguration vorhanden'}
              </pre>
            </Card.Body>
          </Card>
        </Tab>

        {/* Settings Tab */}
        <Tab eventKey="settings" title={<><i className="bi bi-sliders me-1"></i> Einstellungen</>}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>Globale Fail2Ban-Einstellungen</strong>
              <Button variant="primary" size="sm" onClick={handleSaveSettings}>
                <i className="bi bi-save me-1"></i> Speichern
              </Button>
            </Card.Header>
            <Card.Body>
              <Alert variant="info" className="mb-4">
                <i className="bi bi-info-circle me-2"></i>
                Diese Einstellungen gelten als Standard für alle Jails, sofern nicht anders konfiguriert.
              </Alert>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Sperrdauer (bantime)</strong></Form.Label>
                    <Form.Control
                      type="text"
                      value={globalSettings.bantime}
                      onChange={(e) => setGlobalSettings({ ...globalSettings, bantime: e.target.value })}
                      placeholder="z.B. 1h, 24h, 7d"
                    />
                    <Form.Text className="text-muted">
                      Wie lange eine IP gesperrt bleibt (z.B. 10m, 1h, 24h, 7d, -1 für permanent)
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Zeitfenster (findtime)</strong></Form.Label>
                    <Form.Control
                      type="text"
                      value={globalSettings.findtime}
                      onChange={(e) => setGlobalSettings({ ...globalSettings, findtime: e.target.value })}
                      placeholder="z.B. 10m"
                    />
                    <Form.Text className="text-muted">
                      Zeitraum, in dem Fehlversuche gezählt werden
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Max. Versuche (maxretry)</strong></Form.Label>
                    <Form.Control
                      type="number"
                      min={1}
                      max={100}
                      value={globalSettings.maxretry}
                      onChange={(e) => setGlobalSettings({ ...globalSettings, maxretry: parseInt(e.target.value) || 5 })}
                    />
                    <Form.Text className="text-muted">
                      Anzahl Fehlversuche bis zur Sperre
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Alert variant="warning" className="mt-3 mb-0">
                <i className="bi bi-exclamation-triangle me-2"></i>
                Nach dem Speichern muss Fail2Ban neu geladen werden, damit die Änderungen wirksam werden.
              </Alert>
            </Card.Body>
          </Card>

          {/* Recidive Settings */}
          <Card className="mt-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong><i className="bi bi-arrow-repeat me-2"></i>Recidive (Wiederholungstäter)</strong>
              <Button variant="primary" size="sm" onClick={handleSaveRecidive}>
                <i className="bi bi-save me-1"></i> Speichern
              </Button>
            </Card.Header>
            <Card.Body>
              <Alert variant="info" className="mb-4">
                <i className="bi bi-info-circle me-2"></i>
                <strong>Recidive</strong> überwacht wiederholt gesperrte IPs. Wenn eine IP mehrmals gebannt wird,
                erfolgt eine längere Sperre auf <strong>allen Ports</strong>.
              </Alert>

              <Form.Check
                type="switch"
                id="recidive-enabled"
                label={<strong>Recidive aktivieren</strong>}
                checked={recidiveSettings.enabled}
                onChange={(e) => setRecidiveSettings({ ...recidiveSettings, enabled: e.target.checked })}
                className="mb-4"
              />

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Sperrdauer</strong></Form.Label>
                    <Form.Control
                      type="text"
                      value={recidiveSettings.bantime}
                      onChange={(e) => setRecidiveSettings({ ...recidiveSettings, bantime: e.target.value })}
                      placeholder="z.B. 1w"
                      disabled={!recidiveSettings.enabled}
                    />
                    <Form.Text className="text-muted">
                      Wie lange Wiederholungstäter gesperrt werden (z.B. 1w = 1 Woche)
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Zeitfenster</strong></Form.Label>
                    <Form.Control
                      type="text"
                      value={recidiveSettings.findtime}
                      onChange={(e) => setRecidiveSettings({ ...recidiveSettings, findtime: e.target.value })}
                      placeholder="z.B. 1w"
                      disabled={!recidiveSettings.enabled}
                    />
                    <Form.Text className="text-muted">
                      Zeitraum für Zählung der Bans (sollte mind. so lang wie erste Sperrdauer sein)
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Max. Bans</strong></Form.Label>
                    <Form.Control
                      type="number"
                      min={2}
                      max={20}
                      value={recidiveSettings.maxretry}
                      onChange={(e) => setRecidiveSettings({ ...recidiveSettings, maxretry: parseInt(e.target.value) || 3 })}
                      disabled={!recidiveSettings.enabled}
                    />
                    <Form.Text className="text-muted">
                      Nach wie vielen Bans Recidive greift
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              {recidiveSettings.enabled && (
                <Alert variant="success" className="mt-3 mb-0">
                  <i className="bi bi-check-circle me-2"></i>
                  <strong>Aktiv:</strong> IPs die {recidiveSettings.maxretry}x innerhalb von {recidiveSettings.findtime} gebannt werden,
                  werden für {recidiveSettings.bantime} auf allen Ports gesperrt.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* Statistics Tab */}
        <Tab eventKey="statistics" title={<><i className="bi bi-bar-chart me-1"></i> Statistiken</>}>
          <Card className="mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>Ban-Statistiken</strong>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={loadStatistics}
                disabled={statsLoading}
              >
                {statsLoading ? (
                  <><Spinner animation="border" size="sm" className="me-1" /> Laden...</>
                ) : (
                  <><i className="bi bi-arrow-clockwise me-1"></i> Statistiken laden</>
                )}
              </Button>
            </Card.Header>
            <Card.Body>
              {!statistics ? (
                <Alert variant="info">
                  <i className="bi bi-info-circle me-2"></i>
                  Klicken Sie auf "Statistiken laden" um die GeoIP-Analyse zu starten.
                </Alert>
              ) : (
                <>
                  {/* Overview Cards */}
                  <Row className="mb-4">
                    <Col md={3}>
                      <Card className="text-center bg-danger text-white">
                        <Card.Body>
                          <div className="fs-2 fw-bold">{statistics.total_banned_now}</div>
                          <small>Aktuell gesperrt</small>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card className="text-center bg-warning">
                        <Card.Body>
                          <div className="fs-2 fw-bold">{statistics.total_banned_all_time}</div>
                          <small>Gesamt gebannt</small>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card className="text-center bg-secondary text-white">
                        <Card.Body>
                          <div className="fs-2 fw-bold">{statistics.total_failed}</div>
                          <small>Fehlversuche</small>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card className="text-center bg-dark text-white">
                        <Card.Body>
                          <div className="fs-2 fw-bold">{statistics.recidive_banned}</div>
                          <small>Recidive (Wiederholer)</small>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  <Row>
                    {/* Top Attackers */}
                    <Col md={6}>
                      <Card className="mb-4">
                        <Card.Header><strong>Top 10 Angreifer</strong></Card.Header>
                        <Card.Body style={{ maxHeight: '400px', overflow: 'auto' }}>
                          <Table size="sm" striped>
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>IP-Adresse</th>
                                <th>Land</th>
                                <th>Bans</th>
                              </tr>
                            </thead>
                            <tbody>
                              {statistics.top_attackers.map((attacker, idx) => (
                                <tr key={attacker.ip}>
                                  <td>{idx + 1}</td>
                                  <td><code>{attacker.ip}</code></td>
                                  <td>
                                    {attacker.country_code ? (
                                      <span title={attacker.country_name}>
                                        {getCountryFlag(attacker.country_code)} {attacker.country_code}
                                      </span>
                                    ) : '-'}
                                  </td>
                                  <td><Badge bg="danger">{attacker.ban_count}</Badge></td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </Card.Body>
                      </Card>
                    </Col>

                    {/* Countries */}
                    <Col md={6}>
                      <Card className="mb-4">
                        <Card.Header><strong>Angriffe nach Land</strong></Card.Header>
                        <Card.Body style={{ maxHeight: '400px', overflow: 'auto' }}>
                          <Table size="sm" striped>
                            <thead>
                              <tr>
                                <th>Land</th>
                                <th>Anzahl IPs</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.values(statistics.bans_by_country).map((country) => (
                                <tr key={country.code}>
                                  <td>
                                    {getCountryFlag(country.code)} {country.name}
                                  </td>
                                  <td><Badge bg="secondary">{country.count}</Badge></td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  {/* Bans by Jail */}
                  <Card>
                    <Card.Header><strong>Bans pro Jail</strong></Card.Header>
                    <Card.Body>
                      <Table striped>
                        <thead>
                          <tr>
                            <th>Jail</th>
                            <th>Aktuell gesperrt</th>
                            <th>Gesamt gebannt</th>
                            <th>Fehlversuche</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(statistics.bans_by_jail).map(([jailName, data]) => (
                            <tr key={jailName}>
                              <td><strong>{jailName}</strong></td>
                              <td><Badge bg="danger">{data.currently_banned}</Badge></td>
                              <td><Badge bg="warning" text="dark">{data.total_banned}</Badge></td>
                              <td><Badge bg="secondary">{data.total_failed}</Badge></td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* IP Blocking Tab */}
        <Tab eventKey="ipblock" title={<><i className="bi bi-globe me-1"></i> IP-Blocking</>}>
          <Card className="mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong><i className="bi bi-geo-alt me-2"></i>Länder- & IP-Blocking</strong>
              <div>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="me-2"
                  onClick={loadIpBlockStatus}
                  disabled={ipBlockLoading}
                >
                  <i className="bi bi-arrow-clockwise me-1"></i> Aktualisieren
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleUpdateIpBlock}
                  disabled={ipBlockLoading}
                >
                  {ipBlockLoading ? (
                    <><Spinner animation="border" size="sm" className="me-1" /> Lädt...</>
                  ) : (
                    <><i className="bi bi-cloud-download me-1"></i> Listen aktualisieren</>
                  )}
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              <Alert variant="info" className="mb-4">
                <i className="bi bi-info-circle me-2"></i>
                <strong>IP-Blocking</strong> sperrt SIP-Traffic (Port 5060/5061) von bestimmten Ländern oder bekannten Angreifern
                auf Firewall-Ebene (iptables/ipset). Dies ist effektiver als Fail2Ban, da die Pakete sofort verworfen werden.
              </Alert>

              {!ipBlockStatus ? (
                <div className="text-center py-4">
                  <Button variant="primary" onClick={loadIpBlockStatus} disabled={ipBlockLoading}>
                    {ipBlockLoading ? (
                      <><Spinner animation="border" size="sm" className="me-1" /> Laden...</>
                    ) : (
                      <><i className="bi bi-shield-lock me-1"></i> IP-Blocking Status laden</>
                    )}
                  </Button>
                </div>
              ) : (
                <>
                  {/* blocklist.de Section */}
                  <Card className="mb-4">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>blocklist.de</strong>
                        <small className="text-muted ms-2">Bekannte SIP-Angreifer aus Community-Liste</small>
                      </div>
                      <Form.Check
                        type="switch"
                        id="blocklist-de-switch"
                        label={ipBlockStatus.blocklist_de_enabled ? 'Aktiviert' : 'Deaktiviert'}
                        checked={ipBlockStatus.blocklist_de_enabled}
                        onChange={(e) => handleToggleBlocklistDe(e.target.checked)}
                        disabled={ipBlockLoading}
                      />
                    </Card.Header>
                    {ipBlockStatus.blocklist_de_enabled && ipBlockStatus.blocklist_de && (
                      <Card.Body>
                        <Row>
                          <Col md={4}>
                            <div className="text-center">
                              <div className="fs-4 fw-bold text-primary">{ipBlockStatus.blocklist_de.ips}</div>
                              <small className="text-muted">Gesperrte IPs</small>
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="text-center">
                              <div className="fs-4 fw-bold text-danger">{ipBlockStatus.blocklist_de.packets_blocked.toLocaleString()}</div>
                              <small className="text-muted">Pakete blockiert</small>
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="text-center">
                              <div className="fs-4 fw-bold text-secondary">{formatBytes(ipBlockStatus.blocklist_de.bytes_blocked)}</div>
                              <small className="text-muted">Daten blockiert</small>
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    )}
                  </Card>

                  {/* Spamhaus DROP/EDROP Section */}
                  <Card className="mb-4">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>Spamhaus DROP/EDROP</strong>
                        <small className="text-muted ms-2">Hijacked IP-Bereiche und Spam-Netzwerke</small>
                      </div>
                      <Form.Check
                        type="switch"
                        id="spamhaus-switch"
                        label={ipBlockStatus.spamhaus_drop_enabled ? 'Aktiviert' : 'Deaktiviert'}
                        checked={ipBlockStatus.spamhaus_drop_enabled}
                        onChange={(e) => handleToggleSpamhaus(e.target.checked)}
                        disabled={ipBlockLoading}
                      />
                    </Card.Header>
                    {ipBlockStatus.spamhaus_drop_enabled && ipBlockStatus.spamhaus_drop && (
                      <Card.Body>
                        <Row>
                          <Col md={4}>
                            <div className="text-center">
                              <div className="fs-4 fw-bold text-primary">{ipBlockStatus.spamhaus_drop.ranges}</div>
                              <small className="text-muted">IP-Bereiche</small>
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="text-center">
                              <div className="fs-4 fw-bold text-danger">{ipBlockStatus.spamhaus_drop.packets_blocked.toLocaleString()}</div>
                              <small className="text-muted">Pakete blockiert</small>
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="text-center">
                              <div className="fs-4 fw-bold text-secondary">{formatBytes(ipBlockStatus.spamhaus_drop.bytes_blocked)}</div>
                              <small className="text-muted">Daten blockiert</small>
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    )}
                  </Card>

                  {/* FireHOL Level 1 Section */}
                  <Card className="mb-4">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>FireHOL Level 1</strong>
                        <small className="text-muted ms-2">Aggregierte Blocklist von mehreren Quellen</small>
                      </div>
                      <Form.Check
                        type="switch"
                        id="firehol-switch"
                        label={ipBlockStatus.firehol_level1_enabled ? 'Aktiviert' : 'Deaktiviert'}
                        checked={ipBlockStatus.firehol_level1_enabled}
                        onChange={(e) => handleToggleFirehol(e.target.checked)}
                        disabled={ipBlockLoading}
                      />
                    </Card.Header>
                    {ipBlockStatus.firehol_level1_enabled && ipBlockStatus.firehol_level1 && (
                      <Card.Body>
                        <Row>
                          <Col md={4}>
                            <div className="text-center">
                              <div className="fs-4 fw-bold text-primary">{ipBlockStatus.firehol_level1.ranges}</div>
                              <small className="text-muted">IP-Bereiche</small>
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="text-center">
                              <div className="fs-4 fw-bold text-danger">{ipBlockStatus.firehol_level1.packets_blocked.toLocaleString()}</div>
                              <small className="text-muted">Pakete blockiert</small>
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="text-center">
                              <div className="fs-4 fw-bold text-secondary">{formatBytes(ipBlockStatus.firehol_level1.bytes_blocked)}</div>
                              <small className="text-muted">Daten blockiert</small>
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    )}
                  </Card>

                  {/* Country Blocking Section */}
                  <Card>
                    <Card.Header>
                      <strong><i className="bi bi-flag me-2"></i>Länder-Blocking</strong>
                    </Card.Header>
                    <Card.Body>
                      {/* Add Country Form */}
                      <Form className="mb-4" onSubmit={(e) => { e.preventDefault(); handleAddCountryBlock(); }}>
                        <Row>
                          <Col md={8}>
                            <Form.Select
                              value={selectedCountryToBlock}
                              onChange={(e) => setSelectedCountryToBlock(e.target.value)}
                              disabled={ipBlockLoading}
                            >
                              <option value="">-- Land auswählen --</option>
                              {Object.entries(ipBlockStatus.available_countries)
                                .filter(([code]) => !ipBlockStatus.blocked_countries.includes(code))
                                .sort((a, b) => a[1].localeCompare(b[1]))
                                .map(([code, name]) => (
                                  <option key={code} value={code}>
                                    {getCountryFlag(code)} {name} ({code})
                                  </option>
                                ))
                              }
                            </Form.Select>
                          </Col>
                          <Col md={4}>
                            <Button
                              variant="danger"
                              onClick={handleAddCountryBlock}
                              disabled={!selectedCountryToBlock || ipBlockLoading}
                              className="w-100"
                            >
                              <i className="bi bi-plus-lg me-1"></i> Land sperren
                            </Button>
                          </Col>
                        </Row>
                      </Form>

                      {/* Blocked Countries List */}
                      {ipBlockStatus.blocked_countries.length === 0 ? (
                        <Alert variant="secondary">
                          <i className="bi bi-info-circle me-2"></i>
                          Keine Länder gesperrt. Wählen Sie oben ein Land aus, um es zu sperren.
                        </Alert>
                      ) : (
                        <Table striped hover>
                          <thead>
                            <tr>
                              <th>Land</th>
                              <th className="text-center">IP-Bereiche</th>
                              <th className="text-center">Pakete blockiert</th>
                              <th className="text-center">Daten</th>
                              <th style={{ width: '120px' }}>Aktion</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ipBlockStatus.blocked_countries.map(code => {
                              const country = ipBlockStatus.countries[code]
                              const name = ipBlockStatus.available_countries[code] || code
                              return (
                                <tr key={code}>
                                  <td>
                                    <span className="fs-5 me-2">{getCountryFlag(code)}</span>
                                    {name} <Badge bg="secondary">{code}</Badge>
                                  </td>
                                  <td className="text-center">
                                    <Badge bg="info">{country?.ranges?.toLocaleString() || 0}</Badge>
                                  </td>
                                  <td className="text-center">
                                    <Badge bg="danger">{country?.packets_blocked?.toLocaleString() || 0}</Badge>
                                  </td>
                                  <td className="text-center">
                                    {formatBytes(country?.bytes_blocked || 0)}
                                  </td>
                                  <td>
                                    <Button
                                      variant="outline-warning"
                                      size="sm"
                                      onClick={() => handleRemoveCountryBlock(code)}
                                      disabled={ipBlockLoading}
                                    >
                                      <i className="bi bi-unlock me-1"></i> Entsperren
                                    </Button>
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </Table>
                      )}

                      {ipBlockStatus.last_update && (
                        <small className="text-muted">
                          <i className="bi bi-clock me-1"></i>
                          Letzte Aktualisierung: {new Date(ipBlockStatus.last_update).toLocaleString('de-DE')}
                        </small>
                      )}
                    </Card.Body>
                  </Card>
                </>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* Config Tab */}
        <Tab eventKey="config" title={<><i className="bi bi-gear me-1"></i> Konfiguration</>}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>jail.local</strong>
              <Button variant="primary" size="sm" onClick={handleSaveConfig}>
                <i className="bi bi-save me-1"></i> Speichern
              </Button>
            </Card.Header>
            <Card.Body>
              <Form.Control
                as="textarea"
                rows={20}
                value={config}
                onChange={(e) => setConfig(e.target.value)}
                style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
              />
              <Form.Text className="text-muted">
                Nach dem Speichern muss Fail2Ban neu geladen werden, damit die Änderungen wirksam werden.
              </Form.Text>
            </Card.Body>
          </Card>
        </Tab>

        {/* Logs Tab */}
        <Tab eventKey="logs" title={<><i className="bi bi-file-text me-1"></i> Logs</>}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>Fail2Ban Log</strong>
              <Button variant="outline-primary" size="sm" onClick={loadData}>
                <i className="bi bi-arrow-clockwise me-1"></i> Aktualisieren
              </Button>
            </Card.Header>
            <Card.Body>
              <pre style={{
                maxHeight: '500px',
                overflow: 'auto',
                fontSize: '0.8rem',
                backgroundColor: '#1e1e1e',
                color: '#d4d4d4',
                padding: '1rem',
                borderRadius: '4px'
              }}>
                {logs.length > 0 ? logs.join('\n') : 'Keine Log-Einträge'}
              </pre>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Ban IP Modal */}
      <Modal show={showBanModal} onHide={() => setShowBanModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>IP manuell sperren</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Jail</Form.Label>
            <Form.Select
              value={selectedJail}
              onChange={(e) => setSelectedJail(e.target.value)}
            >
              <option value="">-- Jail wählen --</option>
              {jails.map(jail => (
                <option key={jail.name} value={jail.name}>{jail.name}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>IP-Adresse</Form.Label>
            <Form.Control
              type="text"
              placeholder="z.B. 192.168.1.100"
              value={banIp}
              onChange={(e) => setBanIp(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBanModal(false)}>Abbrechen</Button>
          <Button variant="danger" onClick={handleBan}>Sperren</Button>
        </Modal.Footer>
      </Modal>

      {/* Create Jail Modal */}
      <Modal show={showCreateJailModal} onHide={() => setShowCreateJailModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Neues Jail erstellen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Vorlage verwenden</Form.Label>
            <Form.Select onChange={(e) => e.target.value && applyTemplate(e.target.value)}>
              <option value="">-- Keine Vorlage --</option>
              {Object.keys(templates).map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <hr />

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={newJail.name}
                  onChange={(e) => setNewJail({ ...newJail, name: e.target.value })}
                  placeholder="z.B. my-custom-jail"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Port(s)</Form.Label>
                <Form.Control
                  type="text"
                  value={newJail.port}
                  onChange={(e) => setNewJail({ ...newJail, port: e.target.value })}
                  placeholder="z.B. ssh oder 22,2222"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Filter</Form.Label>
                <Form.Control
                  type="text"
                  value={newJail.filter}
                  onChange={(e) => setNewJail({ ...newJail, filter: e.target.value })}
                  placeholder="z.B. sshd"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Log-Pfad</Form.Label>
                <Form.Control
                  type="text"
                  value={newJail.logpath}
                  onChange={(e) => setNewJail({ ...newJail, logpath: e.target.value })}
                  placeholder="z.B. /var/log/auth.log"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Max. Versuche</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  max={100}
                  value={newJail.maxretry}
                  onChange={(e) => setNewJail({ ...newJail, maxretry: parseInt(e.target.value) || 5 })}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Zeitfenster</Form.Label>
                <Form.Control
                  type="text"
                  value={newJail.findtime}
                  onChange={(e) => setNewJail({ ...newJail, findtime: e.target.value })}
                  placeholder="z.B. 10m"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Sperrdauer</Form.Label>
                <Form.Control
                  type="text"
                  value={newJail.bantime}
                  onChange={(e) => setNewJail({ ...newJail, bantime: e.target.value })}
                  placeholder="z.B. 1h oder 24h"
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Check
            type="switch"
            label="Aktiviert"
            checked={newJail.enabled}
            onChange={(e) => setNewJail({ ...newJail, enabled: e.target.checked })}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateJailModal(false)}>Abbrechen</Button>
          <Button variant="primary" onClick={handleCreateJail}>Erstellen</Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
